<template>
    <div>
        <a-table
            :key="'vtable-' + renderCount"
            :columns="cols()"
            :dataSource="getFilteredRows()"
            :loading="isBusy"
            :locale="{
                emptyText: $t('message.noNewUser'),
            }"
            :pagination="false"
            class="custom-table2"
            rowKey="_id"
            @change="tableChange"
            :bordered="true"
        >
            <div :key="'roleSlot' + $i18n.locale" :slot="'roleSlot'" slot-scope="data, item">
                {{ null != item.role ? item.role.name : "-" }}
            </div>
            <div
                :key="'departmentSlot' + $i18n.locale"
                :slot="'departmentSlot'"
                slot-scope="data, item"
                class="departmentSlot"
                :content="getUserDepartmentNames(item)"
                v-tippy="{
                    followCursor: true,
                    placement: 'top-right',
                }"
            >
                {{ getUserDepartmentNames(item) }}
            </div>
            <div :key="'enableStateSlot' + $i18n.locale" :slot="'enableStateSlot'" slot-scope="data, item">
                <a-icon type="check" class="text-success" v-if="item.enable" />
                <a-icon type="close" class="text-danger" v-else />
            </div>
            <div :key="'lastLoginPlatformSlot' + $i18n.locale" :slot="'lastLoginPlatformSlot'" slot-scope="data, item">
                <div v-if="item.lastLoginPlatform === 'mobile'" class="mr-2 text-primary">
                    <a-icon type="mobile" />
                    {{ $t("message.userListLastLoginMobile") }}
                </div>
                <div v-else>
                    <a-icon type="chrome" class="mr-2 text-primary" />
                    {{ $t("message.userListLastLoginWeb") }}
                </div>
            </div>
            <div :key="'statusSlot' + $i18n.locale" :slot="'statusSlot'" slot-scope="data, item">
                <span
                    :class="statuses.find((s) => s.status === item.status).class"
                    class="st-item onlineMode"
                    v-if="item.isOnline"
                >
                    <font-awesome-icon :icon="statuses.find((s) => s.status === item.status).icon" class="mr-2" />
                    {{ $t(statuses.find((s) => s.status === item.status).langKey) }}
                </span>
                <span :class="statuses.find((s) => s.status === item.status).class" class="st-item offlineMode" v-else>
                    <font-awesome-icon :icon="statuses.find((s) => s.status === item.status).icon" class="mr-2" />
                    {{ $t(statuses.find((s) => s.status === item.status).langKey) }}
                </span>
            </div>
            <div :key="'actionsSlot' + $i18n.locale" :slot="'actionsSlot'" slot-scope="data, item">
                <div class="d-flex w-100 justify-content-start"></div>
            </div>
        </a-table>
        <div class="mt-3 d-flex justify-content-end">
            <Pagination :page-change="pageChange" :page-sizes="pageSizes" :total="users.total" />
        </div>
    </div>
</template>

<script>
import Pagination from "./Pagination";
import ActionEdit from "./svg/ActionEdit";
import ActionDelete from "./svg/ActionDelete";
import Multiselect from "vue-multiselect";
import { mapGetters, mapState } from "vuex";
import { log2 } from "../utils/log2";
import { tableSorter } from "../utils/utils";
import { getRolesBySiteId } from "../api/role";
import { getSubjectsBySiteId } from "../api/subject";

const { USER_API } = require("../api/user");
const log = log2("UserList");
export default {
    name: "UserList",
    components: {
        Pagination,
        ActionEdit,
        ActionDelete,
        Multiselect,
    },
    props: {
        siteId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            pageSizes: ["10", "25", "50", "100"],
            statuses: [
                {
                    status: "Online",
                    langKey: "message.online",
                    class: "online",
                    icon: "check-circle",
                    order: 1,
                },
                {
                    status: "Away",
                    langKey: "message.beRightBack",
                    class: "away",
                    icon: "clock",
                    order: 2,
                },
                {
                    status: "Busy",
                    langKey: "message.busy",
                    class: "busy",
                    icon: "minus-circle",
                    order: 3,
                },
                {
                    status: "Offline",
                    langKey: "message.offline",
                    class: "offline",
                    icon: "power-off",
                    order: 4,
                },
            ],
            cols: () => [
                {
                    dataIndex: "name",
                    title: this.$t("message.username"),
                    ...tableSorter("name", "string", this.currentSort),
                },
                {
                    dataIndex: "email",
                    title: this.$t("message.email"),
                    ...tableSorter("email", "string", this.currentSort),
                },
                {
                    dataIndex: "enable",
                    title: this.$t("message.enableState"),
                    scopedSlots: { customRender: "enableStateSlot" },
                    ...tableSorter("enable", "string", this.currentSort),
                },
                {
                    dataIndex: "status",
                    title: this.$t("message.status"),
                    scopedSlots: { customRender: "statusSlot" },
                    ...tableSorter("status", "string", this.currentSort),
                },
                {
                    dataIndex: "lastLoginPlatform",
                    title: "Platform",
                    scopedSlots: { customRender: "lastLoginPlatformSlot" },
                    ...tableSorter("lastLoginPlatform", "string", this.currentSort),
                },
                {
                    dataIndex: "role",
                    title: this.$t("message.role"),
                    scopedSlots: { customRender: "roleSlot" },
                    ...tableSorter("role", "string", this.currentSort),
                },
                {
                    dataIndex: "subjects",
                    title: this.$t("message.departments"),
                    scopedSlots: { customRender: "departmentSlot" },
                    ...tableSorter("subjects", "array_len", this.subjects),
                },
                {
                    title: this.$t("message.transactions"),
                    scopedSlots: { customRender: "actionsSlot" },
                    width: "10%",
                },
            ],
            perPage: 10,
            page: 1,
            renderCount: 0,
            currentSort: {
                columnKey: "enable",
                order: "ascend",
            },
            roles: [],
            subjects: [],
            users: {
                total: 0,
                count: 0,
                data: [],
            },
            isBusy: false,
        };
    },
    computed: {
        ...mapState({
            isAdmin: (state) => state.auth.adminUser != null,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
    },
    methods: {
        getUserDepartmentNames(user) {
            return (user.subjects || []).length
                ? this.subjects
                      .filter((x) => (user.subjects || []).includes(x._id))
                      .map((x) => x.languageContent[this.$i18n.locale].name)
                      .join(",")
                      .toLowerCase()
                : this.$t("message.allDepartments");
        },
        tableChange(pagination, filters, sorter) {
            log("tableChange", { pagination, filters, sorter });
            this.perPage = pagination.pageSize;
            log("tableChange", { pagination, filters, sorter });
            const newSort = {
                columnKey: sorter.columnKey,
                order: sorter.order,
            };
            const oldSort = this.currentSort;
            log("show me current Sort", {
                currentSort: this.currentSort,
                newSort,
            });
            if (!_.isEqual(newSort, oldSort)) {
                this.currentSort = newSort;
                this.renderCount += 1;
                this.$forceUpdate();
            }
        },
        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        getFilteredRows() {
            const globalRegex = RegExp((this.searchText || "").trim().toLowerCase(), "gi");
            return this.searchText
                ? this.users.data.filter(
                      (x) => globalRegex.test(x.name) || globalRegex.test(x.email) || globalRegex.test(x.status)
                  )
                : this.users.data;
        },
        pageChange(page, pageSize) {
            log("pageChange", { page, pageSize });
            this.page = page;
            this.perPage = pageSize;
        },
        onShowSizeChange(current, pageSize) {
            log("onShowSizeChange", current, pageSize);
            this.page = current;
            this.perPage = pageSize;
        },
        async get(getRoles = false, showLoading = true) {
            if (showLoading) this.isBusy = true;
            const query = this.searchText === "" && this.searchText == null ? undefined : this.searchText;
            await USER_API.getUsersBySiteId(this.siteId, this.perPage, this.page, query)
                .then(async (x) => {
                    log.debug("users fetched", x);
                    if (!_.isEqual(this.users, x.data)) {
                        this.users = x.data;
                    }

                    if (getRoles) {
                        await getRolesBySiteId(this.siteId, 100, 1)
                            .then((res) => {
                                log.debug("roles fetched", res);
                                this.roles = res.data.data;
                            })
                            .catch((err) => {
                                log.error("roles fetch error", err);
                            });
                    }
                })
                .finally(() => {
                    if (showLoading) this.isBusy = false;
                });
        },
    },
    mounted() {
        getSubjectsBySiteId(this.siteId, 1000, 1)
            .then((res) => {
                this.subjects = res.data;
            })
            .catch((err) => {
                log.error("subjects fetch error", err);
            });
        this.get(true);

        this.timerId = setInterval(() => {
            this.get(false, false);
        }, 5000);
    },
    beforeDestroy() {
        if (this.timerId) clearInterval(this.timerId);
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        page(newValue, oldValue) {
            if (newValue) {
                this.get();
            }
        },
        perPage(newValue, oldValue) {
            if (newValue) {
                this.get();
            }
        },
        searchText(newValue, oldValue) {
            log("searchText changed", newValue);

            this.get();
        },
    },
};
</script>

<style scoped></style>
